import { _decorator, Node, Vec3, Vec2, Size, CCBoolean, CCFloat, CCInteger, tween, v3, Tween, UITransform } from 'cc';
import { DEV, EDITOR } from 'cc/env';
import Delay from '../../Utils/Delay';
import MultiThemeComponent from './MultiThemeComponent';

const { ccclass, property, menu } = _decorator;

@ccclass('MultiThemeNode')
@menu('MultiTheme/MultiThemeNode')
export default class MultiThemeNode extends MultiThemeComponent<Node> {
    @property
    private readonly updateActive: boolean = false;

    @property({
        visible() {
            return this.updateActive && this.useTween;
        },
    })
    private readonly setActiveImmediately: boolean = false;

    @property({
        type: [CCBoolean],
        visible() {
            return this.updateActive;
        },
    })
    private readonly actives: boolean[] = [];

    @property
    private readonly updatePosition: boolean = false;

    @property({
        visible() {
            return this.updatePosition;
        },
    })
    private readonly updatePositionX: boolean = true;

    @property({
        visible() {
            return this.updatePosition;
        },
    })
    private readonly updatePositionY: boolean = true;

    @property({
        type: [Vec3],
        visible() {
            return this.updatePosition;
        },
    })
    private readonly positions: Vec3[] = [];

    @property
    private readonly updateScale: boolean = false;

    @property({
        visible() {
            return this.updateScale;
        },
    })
    private readonly updateScaleX: boolean = true;

    @property({
        visible() {
            return this.updateScale;
        },
    })
    private readonly updateScaleY: boolean = true;

    @property({
        type: [Vec3],
        visible() {
            return this.updateScale;
        },
    })
    private readonly scales: Vec3[] = [];

    @property
    private readonly updateAngle: boolean = false;

    @property({
        type: [CCFloat],
        visible() {
            return this.updateAngle;
        },
    })
    private readonly angles: number[] = [];

    @property
    private readonly updateContentSize: boolean = false;

    @property({
        visible() {
            return this.updateContentSize;
        },
    })
    private readonly updateContentSizeWidth: boolean = true;

    @property({
        visible() {
            return this.updateContentSize;
        },
    })
    private readonly updateContentSizeHeight: boolean = true;

    @property({
        type: [Size],
        visible() {
            return this.updateContentSize;
        },
    })
    private readonly contentSizes: Size[] = [];

    @property
    private readonly updateAnchor: boolean = false;

    @property({
        visible() {
            return this.updateAnchor;
        },
    })
    private readonly updateAnchorX: boolean = true;

    @property({
        visible() {
            return this.updateAnchor;
        },
    })
    private readonly updateAnchorY: boolean = true;

    @property({
        type: [Vec2],
        visible() {
            return this.updateAnchor;
        },
    })
    private readonly anchors: Vec2[] = [];

    @property
    private readonly updateOpacity: boolean = false;

    @property({
        type: [CCInteger],
        visible() {
            return this.updateOpacity;
        },
    })
    private readonly opacities: number[] = [];

    @property({ tooltip: DEV && '切換 Theme 時是否使用 Tween' })
    private readonly useTween: boolean = false;

    @property({
        type: [CCInteger],
        tooltip: DEV && '從當前 Theme 切換的 Tween 時長(ms)',
        visible() {
            return this.useTween;
        },
    })
    private readonly tweenDuration: number[] = [];

    constructor(name?: string) {
        super(name);
        if (!EDITOR) {
            // eslint-disable-next-line no-constructor-return
            return new Proxy(this, {
                set: (target, p, value) => {
                    // to ensure active is true to register event listener correctly
                    if (p === 'updateActive') {
                        const updateActive = value as boolean;
                        if (updateActive && target.node) {
                            target.node.active = true;
                        }
                    } else if (p === 'node') {
                        const node = value as Node;
                        if (node && target.updateActive) {
                            node.active = true;
                        }
                    }
                    return Reflect.set(target, p, value);
                },
            });
        }
    }

    public getMaxTheme(): number {
        if (this.updateActive) {
            return this.actives.length;
        }
        if (this.updatePosition) {
            return this.positions.length;
        }
        if (this.updateScale) {
            return this.scales.length;
        }
        if (this.updateAngle) {
            return this.angles.length;
        }
        if (this.updateContentSize) {
            return this.contentSizes.length;
        }
        if (this.updateAnchor) {
            return this.anchors.length;
        }
        if (this.updateOpacity) {
            return this.opacities.length;
        }
        return 0;
    }

    public replacePositions(positions: Vec3[]) {
        this.positions.length = 0;
        this.positions.push(...positions);
        this.onThemeChanged();
    }

    public replaceScales(scales: Vec3[]) {
        this.scales.length = 0;
        this.scales.push(...scales);
        this.onThemeChanged();
    }

    public replaceContentSizes(contentSizes: Size[]) {
        this.contentSizes.length = 0;
        this.contentSizes.push(...contentSizes);
        this.onThemeChanged();
    }

    protected getTarget() {
        return this.node;
    }

    protected onThemeChanged(setImmediately: boolean = false) {
        const isUsingTween = !EDITOR && !setImmediately && this.useTween;
        const tweenDuration = isUsingTween ? this.tweenDuration[this.lastTheme] / 1000 : 0;
        if (this.useTween) {
            Tween.stopAllByTarget(this.target);
            Tween.stopAllByTarget(this.target.transform);
        }

        if (this.updateActive) {
            // active 不能用 tween 設定，故另外用 Delay 來處理
            if (tweenDuration && !this.setActiveImmediately) {
                Delay.resolve(tweenDuration * 1000).then(() => {
                    this.target.active = this.actives[this.theme];
                });
            } else {
                this.target.active = this.actives[this.theme];
            }
        }
        if (this.updatePosition) {
            const { x, y } = this.positions[this.theme];
            let { x: posX, y: posY } = this.target.position;
            const { z: posZ } = this.target.position;
            if (this.updatePositionX && this.updatePositionY) {
                posX = x;
                posY = y;
            } else {
                if (this.updatePositionX) {
                    posX = x;
                }
                if (this.updatePositionY) {
                    posY = y;
                }
            }
            this._setParam(tweenDuration, this.target, 'position', v3(posX, posY, posZ));
        }
        if (this.updateScale) {
            const { x, y } = this.scales[this.theme];
            let { x: scaleX, y: scaleY } = this.target.scale;
            const { z: scaleZ } = this.target.scale;
            if (this.updateScaleX && this.updateScaleY) {
                scaleX = x;
                scaleY = y;
            } else {
                if (this.updateScaleX) {
                    scaleX = x;
                }
                if (this.updateScaleY) {
                    scaleY = y;
                }
            }
            this._setParam(tweenDuration, this.target, 'scale', v3(scaleX, scaleY, scaleZ));
        }
        if (this.updateAngle) {
            this._setParam(tweenDuration, this.target, 'angle', this.angles[this.theme]);
        }
        if (this.updateOpacity) {
            this._setParam(tweenDuration, this.target, 'opacity', this.opacities[this.theme]);
        }
        if (this.updateContentSize) {
            const { width, height } = this.contentSizes[this.theme];
            let { width: contentWidth, height: contentHeight } = this.target.transform.contentSize;
            if (this.updateContentSizeWidth && this.updateContentSizeHeight) {
                contentWidth = width;
                contentHeight = height;
            } else if (this.updateContentSizeWidth) {
                contentWidth = width;
            } else {
                contentHeight = height;
            }
            this._setParam(tweenDuration, this.target.transform, 'width', contentWidth);
            this._setParam(tweenDuration, this.target.transform, 'height', contentHeight);
        }
        if (this.updateAnchor) {
            const { x, y } = this.anchors[this.theme];
            let { x: anchorX, y: anchorY } = this.target.transform.anchorPoint;
            if (this.updateAnchorX && this.updateAnchorY) {
                anchorX = x;
                anchorY = y;
            } else if (this.updateAnchorX) {
                anchorX = x;
            } else {
                anchorY = y;
            }
            this._setParam(tweenDuration, this.target.transform, 'anchorX', anchorX);
            this._setParam(tweenDuration, this.target.transform, 'anchorY', anchorY);
        }
    }

    private _setParam<TTarget extends Node | UITransform, TProps extends keyof TTarget>(tweenDuration: number, target: TTarget, prop: TProps, value: TTarget[TProps]) {
        if (tweenDuration) {
            tween(target)
                // @ts-ignore
                .to(tweenDuration, { [prop]: value })
                .start();
        } else {
            target[prop] = value;
        }
    }
}
